/**
 * The CRF Builder's GCDMP quality checks, and the Approve All banner's copy.
 *
 * Lifted out of CRFBuilder.tsx unchanged (plus one new check, below) so it can
 * be tested directly. React-free for the same reason as
 * features/crf/crfBuilderHeader.ts: `web` has no test runner and must not gain
 * one, so its testable logic lives in pure modules exercised from the
 * `functions` package's vitest.
 *
 * Types here are structural slices of CRFBuilder's own — only the fields these
 * checks actually read — so the module never has to import the page.
 */

export interface QualityCheckOption {
  // As the Builder holds it: a number, text (DLM1), or absent. Not read here.
  code?: number | string;
  label: string;
}

export interface QualityCheckVariable {
  varName: string;
  questionText: string;
  responseType?: string;
  options: QualityCheckOption[];
}

export interface QualityCheckForm {
  id: string;
  name: string;
  variables: QualityCheckVariable[] | null;
}

export interface QualityWarning {
  formId: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  field?: string;
  /**
   * For duplicate warnings: the full list of (formId, formName) pairs that
   * contain the duplicated variable. Within-form duplicates have N entries
   * with the same formId; cross-form have entries with distinct formIds.
   * Undefined for non-duplicate warnings.
   *
   * Using formId here (not just name) disambiguates the case of two forms
   * with identical names — the handler can target the exact form even when
   * the UI display uses the shared name.
   */
  duplicateOccurrences?: Array<{ formId: string; formName: string }>;
  /**
   * Distinguishes the two duplicate cases. `intra` = same variable name
   * appears more than once within a single form (always an error, blocks
   * approval). `cross` = same variable name appears in multiple distinct
   * forms (warning only, dismissible by user confirmation; intentional
   * for repeated-measures instruments like the C-SSRS administered at
   * baseline and follow-up). Undefined for non-duplicate warnings.
   */
  duplicateScope?: 'intra' | 'cross';
}

/**
 * The point past which a single form stops being plausible as a form.
 *
 * The largest form in the 42-form MABG packet, once split properly, carries 78
 * variables (Serum Chemistry); the median is 15. 150 sits well clear of any
 * real CRF in the corpus while still catching an amalgamation — the collapsed
 * packet that prompted this check had 840.
 */
export const MAX_VARIABLES_PER_FORM = 150;

/** Run GCDMP quality checks across all forms (client-side). */
/**
 * Concepts whose CDISC shape is FIXED — these are defects, not style.
 *
 * Mirrors functions/src/utils/cdashConceptShape.ts, which is the source of
 * truth and normalises generation. Duplicated across the build boundary because
 * web cannot import from functions; severitySplit.test.ts pins both halves so
 * they cannot drift apart silently.
 */
const SINGLE_ANSWER_VARS = new Set(['AEACN', 'SAACN', 'AEOUT', 'SAOUT', 'AEREL', 'SAREL', 'AESEV']);

function isSeriousnessBundle(v: { varName?: string; questionText?: string; responseType?: string; options?: Array<{ label?: string }> }): boolean {
  if (v.responseType !== 'multi_select') return false;
  const q = String(v.questionText ?? '').toLowerCase();
  const n = String(v.varName ?? '').toUpperCase();
  if (!(/seriousness\s+criteri/.test(q) || n === 'SASR' || n === 'AESERCRIT')) return false;
  const labels = (v.options ?? []).map((o) => String(o?.label ?? '').toLowerCase()).join(' ');
  return ['death', 'life', 'hospital', 'disab', 'congenital', 'medically important']
    .filter((k) => labels.includes(k)).length >= 3;
}

export function isFixedShapeViolation(v: { varName?: string; questionText?: string; responseType?: string; options?: Array<{ label?: string }> }): boolean {
  if (isSeriousnessBundle(v)) return true;
  return v.responseType === 'multi_select'
    && SINGLE_ANSWER_VARS.has(String(v.varName ?? '').toUpperCase());
}

export function fixedShapeReason(v: { varName?: string; responseType?: string; options?: Array<{ label?: string }> }): string {
  if (isSeriousnessBundle(v as never)) {
    return 'Seriousness criteria must be six individual Yes/No variables (AESDTH, AESLIFE, '
      + 'AESHOSP, AESDISAB, AESCONG, AESMIE) per SDTMIG v3.3 §6.2, not one checkbox group.';
  }
  return `${v.varName} is a SINGLE selection from its CDISC codelist; as a checkbox group `
    + 'mutually exclusive answers are simultaneously selectable.';
}

export function runClientQualityChecks(forms: QualityCheckForm[]): QualityWarning[] {
  const warnings: QualityWarning[] = [];

  // Duplicate variable detection — split into two cases:
  //
  //   1. Intra-form: the same variable name appears more than once inside
  //      a single form. Always an error, always blocks approval. Emitted
  //      as one warning per (varName, formId) so the resolution UI can
  //      target each affected form independently.
  //
  //   2. Cross-form: the same variable name appears in multiple distinct
  //      forms. Surfaced as a warning only, never blocks. Often
  //      intentional for repeated-measures instruments (e.g. the C-SSRS
  //      administered at baseline and follow-up). The UI offers a
  //      "Confirm" action that suppresses the warning permanently via
  //      confirmedCrossFormDuplicates on the session.
  //
  // We track formId (not name) so two different forms that happen to share
  // a name aren't misclassified as intra-form. Each occurrence carries
  // both so the UI can display the friendly name but the handlers can
  // target the exact form by id.
  //
  // NOTE, 2026-08-21: neither of these can fire on a form produced by
  // `collapseToSingleForm`, which dedupes by varName before saving and
  // leaves exactly one form behind. That is why the oversize check below
  // exists — the operation that merges a packet also disables the two
  // detectors that would otherwise notice.
  const varMap = new Map<string, Array<{ formId: string; formName: string }>>();
  for (const form of forms) {
    if (!form.variables) continue;
    for (const v of form.variables) {
      if (!v.varName) continue;
      const existing = varMap.get(v.varName) || [];
      existing.push({ formId: form.id, formName: form.name });
      varMap.set(v.varName, existing);
    }
  }
  for (const [varName, occurrences] of varMap) {
    if (occurrences.length <= 1) continue;

    // Group by formId. Any group with >1 entries is an intra-form duplicate
    // for that form. Multiple distinct keys means the variable is also
    // present cross-form.
    const byForm = new Map<string, Array<{ formId: string; formName: string }>>();
    for (const occ of occurrences) {
      const list = byForm.get(occ.formId) || [];
      list.push(occ);
      byForm.set(occ.formId, list);
    }

    // 1. Intra-form errors — one per form that has duplicate copies of this
    //    variable. Carries only that form's occurrences so the resolution
    //    UI can scope its actions correctly.
    for (const [formId, group] of byForm) {
      if (group.length <= 1) continue;
      warnings.push({
        formId,
        severity: 'error',
        message: `Variable "${varName}" is duplicated ${group.length}× within form: ${group[0].formName}`,
        field: varName,
        duplicateOccurrences: group,
        duplicateScope: 'intra',
      });
    }

    // 2. Cross-form warning — one per variable, listing every form it
    //    appears in. Suppressed by confirmCrossFormDuplicate.
    if (byForm.size > 1) {
      // Render each form once with " (#N)" suffix when two physically
      // distinct forms share the same display name.
      const distinctOccs = Array.from(byForm.values()).map((g) => g[0]);
      const nameCounts = new Map<string, number>();
      const labels = distinctOccs.map((o) => {
        const seen = (nameCounts.get(o.formName) || 0) + 1;
        nameCounts.set(o.formName, seen);
        const sharesName = distinctOccs.some(
          (other) => other.formName === o.formName && other.formId !== o.formId,
        );
        return sharesName ? `${o.formName} (#${seen})` : o.formName;
      });
      warnings.push({
        formId: distinctOccs[0].formId,
        severity: 'warning',
        message: `Variable "${varName}" appears in multiple forms: ${labels.join(', ')}. Confirm this is intentional for a repeated-measures instrument.`,
        field: varName,
        duplicateOccurrences: distinctOccs,
        duplicateScope: 'cross',
      });
    }
  }

  for (const form of forms) {
    if (!form.variables) continue;

    // Form shape. Every other check below asks about a variable; this one
    // asks about the form. A CRF carrying several hundred variables is
    // almost always several CRFs wearing one name — the case this was
    // written for merged 42 forms into a single 840-variable form and drew
    // no comment at all from the checks that follow.
    //
    // Warning, not error: an unusually large form is suspicious, not
    // invalid, and a heuristic that blocked approval would cost more than
    // the miss it prevents.
    if (form.variables.length > MAX_VARIABLES_PER_FORM) {
      warnings.push({
        formId: form.id,
        severity: 'warning',
        message:
          `"${form.name}" holds ${form.variables.length} variables, well past the ` +
          `${MAX_VARIABLES_PER_FORM} a single form usually carries. If this was a ` +
          `multi-form packet, re-upload it with "This file is a single CRF" unticked.`,
      });
    }

    for (const v of form.variables) {
      // ── STRUCTURE BLOCKS ──────────────────────────────────────────────
      // A concept whose CDISC shape is FIXED, emitted as a checkbox group, is a
      // defect in the record: seriousness criteria are six named Yes/No
      // variables per SDTMIG v3.3 §6.2, and a single-answer codelist field like
      // AEACN becomes incoherent as checkboxes ("None" and "Withdrawn" both
      // selectable). These block.
      if (isFixedShapeViolation(v)) {
        warnings.push({
          formId: form.id,
          severity: 'error',
          message: `"${v.varName}": ${fixedShapeReason(v)}`,
          field: v.varName,
        });
      }
      // ── STYLE INFORMS ─────────────────────────────────────────────────
      // The phrase alone is a WARNING, demoted from error 2026-08-28.
      //
      // GCDMP §7e genuinely prefers individual Yes/No fields, so this is still
      // said. But it must not STOP a customer: the generator prompt explicitly
      // permits multi_select "for natural grouped checklists", so blocking the
      // phrasing those checklists require made the platform refuse to approve
      // what it had just produced. Measured on SDTMCONF-T3: USVACTN ("actions
      // taken during this visit") and DAMISRSN ("reasons for missed
      // injections") — both legitimately multi-response — blocked approval and
      // stalled every downstream document. severitySplit.test.ts pins this.
      else if (/(?:check|select|mark)\s+all\s+that\s+apply/i.test(v.questionText)) {
        warnings.push({
          formId: form.id,
          severity: 'warning',
          message: `"${v.varName}": Uses "check/select all that apply". GCDMP §7e prefers individual Yes/No questions; confirm a checkbox group is intended here.`,
          field: v.varName,
        });
      }

      // multi_select response type
      if (v.responseType === 'multi_select' && v.options.length > 2) {
        warnings.push({
          formId: form.id,
          severity: 'warning',
          message: `"${v.varName}": Uses multi-select with ${v.options.length} options. Consider individual Yes/No questions.`,
          field: v.varName,
        });
      }

      // Yes/No ordering check
      if (v.responseType === 'yesno' || (v.options.length === 2 &&
          v.options.some(o => o.label.toLowerCase() === 'yes') &&
          v.options.some(o => o.label.toLowerCase() === 'no'))) {
        const yesIdx = v.options.findIndex(o => o.label.toLowerCase() === 'yes');
        if (yesIdx !== 0) {
          warnings.push({
            formId: form.id,
            severity: 'warning',
            message: `"${v.varName}": Yes/No options are in reversed order. "Yes" should appear first.`,
            field: v.varName,
          });
        }
      }

      // Double negative detection
      if (/\bnot\b.*\bnot\b/i.test(v.questionText) || /\bwas\s+(?:this|the|it)\s+not\b/i.test(v.questionText)) {
        warnings.push({
          formId: form.id,
          severity: 'warning',
          message: `"${v.varName}": Question may contain a double negative. Use positive phrasing.`,
          field: v.varName,
        });
      }
    }
  }

  return warnings;
}

/**
 * The parenthetical on the Approve All bar.
 *
 * It used to read "(no quality issues)" whenever nothing BLOCKED approval,
 * which on the collapsed MABG packet meant describing 22 live warnings as no
 * issues at all. Errors and warnings are different claims and the bar now
 * makes only the one it can support.
 */
export function approveBarSummary(warningCount: number): string {
  if (warningCount <= 0) return 'no quality issues';
  return `no blocking issues (${warningCount} warning${warningCount === 1 ? '' : 's'})`;
}
